import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from './SiteHeader';
import useScrollReveal from '../hooks/useScrollReveal';

const GREEN = '#1e5631';
const GREEN_DARK = '#0f3d20';
const GOLD = '#d4af37';

function Reveal({ as: Tag = 'div', className = '', style, children, ...rest }) {
  const ref = useScrollReveal();
  return (
    <Tag ref={ref} className={`reveal ${className}`} style={style} {...rest}>
      {children}
    </Tag>
  );
}

export default function PartnerProgramPage() {
  return (
    <div className="landing-page public-page">
      <SiteHeader />

      {/* ── Hero ── */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '80px 40px 60px', textAlign: 'center' }}>
        <div className="brand-blob" style={{ width: 460, height: 460, top: -180, left: '50%', transform: 'translateX(-50%)', background: GREEN, opacity: 0.5 }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '760px', margin: '0 auto' }}>
          <span style={{
            display: 'inline-block', background: 'rgba(212,175,55,0.15)', color: '#a3791d',
            padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 700,
            letterSpacing: '0.4px', marginBottom: '18px',
          }}>
            💰 Zero Investment · Passive Income
          </span>
          <h1 style={{ fontSize: '46px', color: GREEN_DARK, marginBottom: '18px', fontWeight: 800, lineHeight: 1.15 }}>
            Chill Ras Partner Program
          </h1>
          <p style={{ fontSize: '19px', color: '#5c6b64', maxWidth: '560px', margin: '0 auto' }}>
            Earn extra income with zero investment — host a fully automated Chill Ras kiosk at your shop or space,
            and attract more customers to your business.
          </p>
        </div>
      </section>

      {/* ── Benefit cards ── */}
      <Reveal as="section" style={{ padding: '20px 40px 70px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', maxWidth: '1000px', margin: '0 auto' }}>
          {[
            ['💰', 'Zero Setup Cost', 'No capital investment needed to start.'],
            ['📈', '15% Commission', 'Attractive payout on every cup sold.'],
            ['🔧', 'Zero Effort, Daily Maintenance', 'We clean & stock — full-service maintenance provided.'],
          ].map(([icon, title, desc]) => (
            <div key={title} className="lift-card" style={{
              padding: '32px 26px', backgroundColor: '#fff', borderRadius: '16px',
              boxShadow: '0 6px 24px rgba(15,61,32,0.08)', width: '270px', borderTop: `4px solid ${GOLD}`,
            }}>
              <div style={{ fontSize: '38px', marginBottom: '14px' }}>{icon}</div>
              <h3 style={{ margin: '0 0 8px', color: GREEN_DARK, fontSize: '20px', fontWeight: 800 }}>{title}</h3>
              <p style={{ color: '#5c6b64', margin: 0, fontSize: '14px' }}>{desc}</p>
            </div>
          ))}
        </div>
      </Reveal>

      {/* ── Machine visual + specs ── */}
      <Reveal as="section" style={{ padding: '0 40px 70px' }}>
        <div style={{
          maxWidth: '1000px', margin: '0 auto', backgroundColor: '#fff',
          borderRadius: '20px', padding: '48px', boxShadow: '0 6px 30px rgba(15,61,32,0.08)',
          display: 'flex', gap: '44px', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <img
            src="/machine-product.png"
            alt="Chill Ras kiosk machine"
            style={{ maxWidth: '240px', width: '100%', filter: 'drop-shadow(0 15px 30px rgba(15,61,32,0.22))', flex: '0 0 auto' }}
          />
          <div style={{ flex: '1', minWidth: '280px' }}>
            <h3 style={{ color: GREEN_DARK, marginTop: 0, marginBottom: '26px', fontSize: '22px', fontWeight: 800 }}>Machine Specs</h3>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
              gap: '24px', textAlign: 'left',
            }}>
              {[
                ['⚡', 'Very Low Power', 'Peak wattage: 120W · Regular: 50W'],
                ['📐', 'Compact Dimensions', '70cm × 50cm floor space, 158cm height (incl. wheels)'],
                ['✨', 'Hygienic Production', 'Clean, contact-free dispensing'],
                ['🌿', 'Purity of Ingredients', 'No preservatives, ever'],
                ['🔒', 'Secure, Humanless Process', 'Fully automated, contactless payment'],
                ['🍹', 'Heavenly Recipes', 'Fresh sugarcane, aam panna & more, made with real fruit, ginger, mint, black pepper & more'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '22px' }}>{icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, color: GREEN_DARK, fontSize: '15px' }}>{title}</div>
                    <div style={{ color: '#5c6b64', fontSize: '13px' }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── Flyer visual ── */}
      <Reveal as="section" style={{ padding: '0 40px 70px', textAlign: 'center' }}>
        <img
          src="/partner-flyer.jpg"
          alt="Chill Ras Partner Program details"
          className="lift-card"
          style={{ maxWidth: '360px', width: '100%', borderRadius: '16px', boxShadow: '0 10px 35px rgba(15,61,32,0.15)' }}
        />
      </Reveal>

      {/* ── CTA ── */}
      <Reveal as="section" style={{ padding: '0 40px 90px' }}>
        <div style={{
          background: `linear-gradient(120deg, ${GREEN_DARK}, ${GREEN})`, color: '#fff', borderRadius: '20px',
          padding: '48px', maxWidth: '700px', margin: '0 auto', textAlign: 'center',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '26px', fontWeight: 800 }}>Interested in Partnering?</h3>
          <p style={{ opacity: 0.9, marginBottom: '26px' }}>Reach out and we'll set up your kiosk — hassle-free.</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', fontSize: '15px' }}>
            {['9599419519', '8527250312', '7409102657'].map((num) => (
              <a
                key={num}
                href={`https://wa.me/91${num}`}
                target="_blank"
                rel="noopener noreferrer"
                className="lift-card"
                style={{
                  color: GREEN_DARK, textDecoration: 'none', background: GOLD,
                  padding: '11px 22px', borderRadius: '10px', fontWeight: 700,
                }}
              >
                📞 {num}
              </a>
            ))}
          </div>
          <p style={{ marginTop: '22px', opacity: 0.85 }}>✉️ suadretailautomations@gmail.com</p>
        </div>
      </Reveal>

      {/* ── Footer ── */}
      <section style={{
        backgroundColor: GREEN_DARK, padding: '24px 40px', textAlign: 'center',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
      }}>
        <Link to="/?login=1" style={{
          color: GOLD, textDecoration: 'none', fontWeight: 700, fontSize: '14px',
          border: `1px solid ${GOLD}`, borderRadius: '8px', padding: '8px 20px',
        }}>
          🔐 Admin Login
        </Link>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', margin: 0 }}>
          © 2026 Chill Ras. Made in India 🇮🇳. All rights reserved.
        </p>
      </section>

    </div>
  );
}
